import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { jsx } from '@emotion/react';
import { useCallback, isValidElement } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { useConvertSxToEmotionStyles } from '../../hooks/useConvertSxToEmotionStyles.js';
import { useGetComponentCloneMode } from '../../hooks/useGetComponentCloneMode.js';
import { cloneElementWithCssProp } from '../../utils/componentHelpers.js';

var _excluded = ["testId", "children", "fallBackDomNode", "onClick", "sx", "domRef", "rc"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

// @NOTE: Very tricky to safely type this component,
// as it can accept any number of children, of different dom element types

// ------------------------------------------------------------------------------
// @NOTE: this component allows 3 different render modes:
// 1  - When there is a single react component child, simply clone it and pass
//      down all props as is
//
// 2  - When there is a single DOM component child, clone it, and modify props
//      so that they are suitable to be rendered onto a natural dom element
//
// 3  - When there are multiple children, or the child is a fragment - wrap
//      the children inside a span, and modify props so that they are suitable
//      to be rendered onto a natural dom element
//
function SmartClone(_ref) {
  var testId = _ref.testId,
    children = _ref.children,
    _ref$fallBackDomNode = _ref.fallBackDomNode,
    fallBackDomNode = _ref$fallBackDomNode === void 0 ? "span" : _ref$fallBackDomNode,
    onClick = _ref.onClick,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    domRef = _ref.domRef,
    rc = _ref.rc,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useGetComponentClone = useGetComponentCloneMode(children),
    shouldCloneAsComponent = _useGetComponentClone.shouldCloneAsComponent,
    shouldCloneAsDom = _useGetComponentClone.shouldCloneAsDom;
  var customStyles = useConvertSxToEmotionStyles(sx);
  var mergedOnClicks = useCallback(function (ev) {
    var _children$props, _children$props$onCli;
    if ( /*#__PURE__*/isValidElement(children)) (_children$props = children.props) === null || _children$props === void 0 || (_children$props$onCli = _children$props.onClick) === null || _children$props$onCli === void 0 ? void 0 : _children$props$onCli.call(_children$props, ev);
    onClick === null || onClick === void 0 ? void 0 : onClick(ev);
  }, [onClick, children]);

  // @NOTE: render mode 1
  return shouldCloneAsComponent && /*#__PURE__*/isValidElement(children) ? cloneElementWithCssProp(children, _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, testId ? {
    testId: testId
  } : {}), merge({}, children.props, props)), domRef ? {
    domRef: domRef
  } : {}), onClick || children.props.onClick ? {
    onClick: mergedOnClicks
  } : {}), {}, {
    sx: merge({}, _objectSpread({}, children.props.sx), sx)
  }, rc ? {
    rc: rc
  } : {})) : shouldCloneAsDom && /*#__PURE__*/isValidElement(children) ?
  // @NOTE: render mode 2
  cloneElementWithCssProp(children, _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, testId ? {
    "data-testid": testId
  } : {}), merge({}, children.props, props)), domRef ? {
    ref: domRef
  } : {}), onClick || children.props.onClick ? {
    onClick: mergedOnClicks
  } : {}), children.props.css ? {
    css: [customStyles, children.props.css]
  } : {
    css: customStyles
  })) :
  // @NOTE: render mode 3
  jsx(fallBackDomNode, _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, props), testId ? {
    "data-testid": testId
  } : {}), domRef ? {
    ref: domRef
  } : {}), onClick ? {
    onClick: onClick
  } : {}), {}, {
    css: customStyles,
    children: children
  }));
}
SmartClone.displayName = "SmartClone";

export { SmartClone as S };
//# sourceMappingURL=SmartClone.js.map
